import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { services } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    carModel: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    carModel: '',
    service: '',
    details: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, contactForm);
      toast.success(data.message);
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/booking`, bookingForm);
      toast.success(data.message);
      setBookingForm({ name: '', email: '', phone: '', service: '', date: '', time: '', carModel: '' });
    } catch (error) {
      toast.error('Failed to submit booking. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/quote`, quoteForm);
      toast.success(data.message);
      setQuoteForm({ name: '', email: '', phone: '', carModel: '', service: '', details: '' });
    } catch (error) {
      toast.error('Failed to submit quote request. Please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Get In
              <br />
              <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">
                Touch
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We're here to help with all your premium car service needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Phone,
                title: 'Call Us',
                detail: '+91 99250 25351',
                link: 'tel:+919925025351'
              },
              {
                icon: Phone,
                title: 'Customer Care',
                detail: '+91 88666 89002',
                link: 'tel:+918866689002'
              },
              {
                icon: Mail,
                title: 'Email Us',
                detail: 'info@carzstreet.com',
                link: 'mailto:info@carzstreet.com'
              },
              {
                icon: Clock,
                title: 'Working Hours',
                detail: 'Mon-Sat: 9AM - 7PM',
                link: '#'
              }
            ].map((item, index) => (
              <motion.a
                key={index}
                href={item.link}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 text-center group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.detail}</p>
              </motion.a>
            ))}
          </div>

          {/* Departmental Emails */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12"
          >
            <h3 className="text-center text-xl font-bold text-gray-900 mb-6">Reach the Right Team</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'General Enquiries', email: 'info@carzstreet.com' },
                { label: 'Service & Booking', email: 'service@carzstreet.com' },
                { label: 'Insurance Claims', email: 'insurance@carzstreet.com' },
                { label: 'Accounts & Billing', email: 'accounts@carzstreet.com' }
              ].map((item, i) => (
                <a
                  key={i}
                  href={`mailto:${item.email}`}
                  data-testid={`email-dept-${i}`}
                  className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg border border-gray-100 hover:border-red-600 transition-all duration-300 flex items-center gap-3 group"
                >
                  <div className="w-10 h-10 bg-red-50 group-hover:bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                    <Mail className="w-5 h-5 text-red-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-gray-500 uppercase tracking-wider">{item.label}</div>
                    <div className="text-sm font-semibold text-gray-900 truncate">{item.email}</div>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {[
              { id: 'contact', label: 'Contact Us' },
              { id: 'booking', label: 'Book Service' },
              { id: 'quote', label: 'Get Quote' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-red-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Contact Form */}
          {activeTab === 'contact' && (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleContactSubmit}
              className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <Input
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Your name"
                    className="h-12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    required
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                <Input
                  required
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="h-12"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <Textarea
                  required
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="Tell us how we can help..."
                  rows={5}
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-lg"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send className="ml-2 w-5 h-5" />
              </Button>
            </motion.form>
          )}

          {/* Booking Form */}
          {activeTab === 'booking' && (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleBookingSubmit}
              className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Book Your Service</h2>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <Input
                    required
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                    placeholder="Your name"
                    className="h-12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    required
                    type="email"
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <Input
                    required
                    type="tel"
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    className="h-12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Car Model *</label>
                  <Input
                    required
                    value={bookingForm.carModel}
                    onChange={(e) => setBookingForm({ ...bookingForm, carModel: e.target.value })}
                    placeholder="e.g., BMW 5 Series"
                    className="h-12"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Type *</label>
                <Select
                  required
                  value={bookingForm.service}
                  onValueChange={(value) => setBookingForm({ ...bookingForm, service: value })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.title}>
                        {service.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date *</label>
                  <Input
                    required
                    type="date"
                    value={bookingForm.date}
                    onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value })}
                    className="h-12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time *</label>
                  <Input
                    required
                    type="time"
                    value={bookingForm.time}
                    onChange={(e) => setBookingForm({ ...bookingForm, time: e.target.value })}
                    className="h-12"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-lg"
              >
                {isSubmitting ? 'Booking...' : 'Confirm Booking'}
                <Send className="ml-2 w-5 h-5" />
              </Button>
            </motion.form>
          )}

          {/* Quote Form */}
          {activeTab === 'quote' && (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleQuoteSubmit}
              className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Request a Quote</h2>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <Input
                    required
                    value={quoteForm.name}
                    onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                    placeholder="Your name"
                    className="h-12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    required
                    type="email"
                    value={quoteForm.email}
                    onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                  <Input
                    required
                    type="tel"
                    value={quoteForm.phone}
                    onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    className="h-12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Car Model *</label>
                  <Input
                    required
                    value={quoteForm.carModel}
                    onChange={(e) => setQuoteForm({ ...quoteForm, carModel: e.target.value })}
                    placeholder="e.g., Audi A6"
                    className="h-12"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Needed *</label>
                <Select
                  required
                  value={quoteForm.service}
                  onValueChange={(value) => setQuoteForm({ ...quoteForm, service: value })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.title}>
                        {service.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Details</label>
                <Textarea
                  value={quoteForm.details}
                  onChange={(e) => setQuoteForm({ ...quoteForm, details: e.target.value })}
                  placeholder="Tell us more about your requirements..."
                  rows={4}
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Request Quote'}
                <Send className="ml-2 w-5 h-5" />
              </Button>
            </motion.form>
          )}
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Locations</h2>
            <p className="text-xl text-gray-600">Visit us at any of our workshops</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Head Office */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <span className="inline-block px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full mb-3 uppercase tracking-wider">
                Head Office
              </span>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Ahmedabad</h3>
              <p className="text-gray-600 leading-relaxed">
                Carz Street, PRL colony, Behind Minotti Furniture, Sindhu Bhavan Road, Bodakdev, Ahmedabad-380054 Gujarat
              </p>
            </motion.div>

            {/* Indore Branch */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <span className="inline-block px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full mb-3 uppercase tracking-wider">
                Branch
              </span>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Indore</h3>
              <p className="text-gray-600 leading-relaxed mb-3">
                Plot no. 30, New Bijalpur Main Road, Indore 452012, MP
              </p>
              <a href="tel:+919630541958" className="inline-flex items-center text-red-600 font-semibold hover:text-red-700 transition-colors">
                <Phone className="w-4 h-4 mr-2" />
                +91 96305 41958
              </a>
            </motion.div>

            {/* Coming Soon Branch */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 relative overflow-hidden"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-full flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded-full mb-3 uppercase tracking-wider animate-pulse">
                Coming Soon
              </span>
              <h3 className="text-xl font-bold text-white mb-2">Ahmedabad (Tragad)</h3>
              <p className="text-gray-300 leading-relaxed">
                Carz Street, Opp. Hillock Hotel, Next to Shri Hari Dreamland, Ahmedabad (Tragad) 382470 Gujarat
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;